import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getStats } from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Grid,
  useTheme,
} from "@/components/ui";
import PlanChip from "../../../components/PlanChip";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import AppLoading from "@/components/AppLoading";
import {
  ContractsIcon,
  CheckIcon,
  ClockIcon,
  ExpiredIcon,
  ClientsIcon,
  WebhooksIcon,
  DocIcon,
  CalendarIcon,
} from "@/components/icons/Icons";
import {
  AnimatedNumber,
  AnimatedCountWithLimit,
} from "@/components/AnimatedNumber";

const PERIOD_OPTIONS = [7, 30, 90, 0]; // 0 = All

/** Plan border colors (match PlanChip). Trial: black in light mode, white in dark. */
const getPlanBorder = (planKey, mode) =>
  planKey === "trial"
    ? mode === "dark"
      ? "2px solid #fff"
      : "2px solid #000"
    : {
        professional: "2px solid #1E88E5",
        ultra: "2px solid #FF8A65",
        extreme: "2px solid #AE98FE",
      }[planKey];

/** Limits and display data per plan (aligned with backend where applicable). */
const PLAN_DATA = {
  trial: {
    contractsPerMonth: 50,
    templatesAllowed: 5,
    teamMembers: 2,
    storageLimit: 500,
    priceKey: "trial",
  },
  professional: {
    contractsPerMonth: 500,
    templatesAllowed: 20,
    teamMembers: 10,
    storageLimit: 5000,
    priceKey: "49",
  },
  ultra: {
    contractsPerMonth: 2000,
    templatesAllowed: 50,
    teamMembers: 20,
    storageLimit: 15000,
    priceKey: "99",
  },
  extreme: {
    contractsPerMonth: 10000,
    templatesAllowed: 100,
    teamMembers: 50,
    storageLimit: 50000,
    priceKey: "199",
  },
};

/** Map API plan to dashboard plan key. */
const normalizePlan = (apiPlan) => {
  const p = (apiPlan || "starter").toString().toLowerCase();
  if (p === "starter" || p === "free") return "trial";
  if (p === "enterprise") return "extreme";
  if (p === "professional") return "professional";
  if (p === "ultra") return "ultra";
  return "trial";
};

const CompanyDashboard = () => {
  const theme = useTheme();
  const { t, isRTL } = useApp();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const apiDays = period === 0 ? 3650 : period;
      const data = await getStats(apiDays);
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const usagePercentage = stats?.subscription
    ? Math.round(
        ((stats.usage?.currentPeriod?.contractsCreated || 0) /
          (stats.subscription?.limits?.contractsPerMonth || 100)) *
          100,
      )
    : 0;

  const contractsUsed = stats?.usage?.currentPeriod?.contractsCreated || 0;
  const contractsLimit = stats?.subscription?.limits?.contractsPerMonth || 100;

  const formatDateDDMMYYYY = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    const daysForRange = period === 0 ? 3650 : period;
    start.setDate(end.getDate() - daysForRange);
    return {
      start: formatDateDDMMYYYY(start),
      end: formatDateDDMMYYYY(end),
    };
  };
  const dateRange = getDateRange();

  if (loading) {
    return <AppLoading fullPage />;
  }

  const pieData = stats?.contracts
    ? [
        {
          name: isRTL ? "נחתמו" : "Signed",
          value: stats.contracts.signed || 0,
          color: theme.palette.success.main,
        },
        {
          name: isRTL ? "ממתינים" : "Pending",
          value: (stats.contracts.pending || 0) + (stats.contracts.viewed || 0),
          color: theme.palette.warning.main,
        },
        {
          name: isRTL ? "פגו תוקף" : "Expired",
          value: stats.contracts.expired || 0,
          color: theme.palette.error.main,
        },
        {
          name: isRTL ? "בוטלו" : "Cancelled",
          value: stats.contracts.cancelled || 0,
          color: theme.palette.text.secondary,
        },
      ].filter((item) => item.value > 0)
    : [];

  return (
    <Box>
      {/* Analytics - large card; Plans as first row inside */}
      <CardContent>
        {/* Plans - 4 plans in one row with relevant data; active plan has border + green check */}
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
          {t("plans")}
        </Typography>
        {(() => {
          const activePlan = normalizePlan(stats?.subscription?.plan);
          const planKeys = ["trial", "professional", "ultra", "extreme"];
          return (
            <Grid container spacing={1.5} sx={{ mb: 1.5, width: "100%" }}>
              {planKeys.map((planKey) => {
                const data = PLAN_DATA[planKey];
                const isActive = activePlan === planKey;
                const priceLabel =
                  data.priceKey === "trial"
                    ? isRTL
                      ? "תקופת ניסיון"
                      : "Trial"
                    : isRTL
                      ? `${data.priceKey} ₪ ${t("perMonth")}`
                      : `$${data.priceKey}/${t("perMonth")}`;
                const borderSx =
                  isActive && getPlanBorder(planKey, theme.palette.mode)
                    ? {
                        border: getPlanBorder(planKey, theme.palette.mode),
                        borderWidth: 2,
                      }
                    : {};
                const cardContent = (
                  <CardContent
                    sx={{
                      py: 1,
                      px: 1.5,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      minHeight: 0,
                      "&:last-child": { pb: 1 },
                    }}
                  >
                    {isActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: isRTL ? "auto" : 6,
                          left: isRTL ? 6 : "auto",
                          zIndex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          color: "#fff",
                          "& svg": { width: 12, height: 12 },
                        }}
                        aria-hidden
                      >
                        <CheckIcon />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 0.25,
                      }}
                    >
                      <PlanChip plan={planKey} matchCardBackground />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{ textAlign: "center", display: "block", mb: 0.5 }}
                    >
                      {priceLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{ display: "block" }}
                    >
                      {data.contractsPerMonth.toLocaleString()} {t("contracts")}{" "}
                      {t("perMonth")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{ display: "block" }}
                    >
                      {data.templatesAllowed} {t("templates")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{ display: "block", mb: 0.75 }}
                    >
                      {data.teamMembers} {t("teamMembers")}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        mt: "auto",
                      }}
                    >
                      {isActive ? (
                        <>
                          <Box
                            sx={{
                              py: 0.75,
                              px: 1,
                              borderRadius: 1,
                              bgcolor: "action.hover",
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.primary"
                              sx={{ display: "block" }}
                            >
                              {t("contractsUsedThisMonth")} —{" "}
                              {isRTL
                                ? `${contractsLimit} / ${contractsUsed}`
                                : `${contractsUsed} / ${contractsLimit}`}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(usagePercentage, 100)}
                              sx={{ height: 4, borderRadius: 1, mt: 0.5 }}
                              color={
                                usagePercentage > 90
                                  ? "error"
                                  : usagePercentage > 70
                                    ? "warning"
                                    : "primary"
                              }
                            />
                          </Box>
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{
                                width: "max-content",
                                minHeight: 26,
                                py: 0.25,
                                px: 1,
                                borderRadius: "150px",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              {t("manageSubscription")}
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              width: "max-content",
                              minHeight: 26,
                              py: 0.25,
                              px: 1,
                              borderRadius: "150px",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          >
                            {t("upgradeSubscription")}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                );
                return (
                  <Grid
                    item
                    xs={6}
                    sm={3}
                    key={planKey}
                    sx={{ display: "flex", flex: "1 1 0%", minWidth: 0 }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        minHeight: 200,
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        bgcolor: "background.paper",
                        ...borderSx,
                      }}
                    >
                      {cardContent}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ direction: isRTL ? "rtl" : "ltr" }}
          >
            <Typography variant="subtitle1" fontWeight={600} component="span">
              {isRTL ? (
                <>
                  מ־{dateRange.start}
                  <Typography
                    component="span"
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mx: 0.5 }}
                  >
                    –
                  </Typography>
                  עד {dateRange.end}
                </>
              ) : (
                `${dateRange.start} - ${dateRange.end}`
              )}
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(e, v) => v != null && setPeriod(v)}
            size="small"
            aria-label="period"
            sx={{
              "& .MuiToggleButton-root": { px: 1.75 },
              ...(isRTL && {
                flexDirection: "row-reverse",
                "& .MuiToggleButtonGroup-grouped": {
                  flexDirection: "row-reverse",
                },
              }),
            }}
          >
            {(isRTL ? [...PERIOD_OPTIONS].reverse() : PERIOD_OPTIONS).map(
              (d) => (
                <ToggleButton
                  key={d}
                  value={d}
                  aria-label={
                    d === 0
                      ? isRTL
                        ? "הכל"
                        : "All"
                      : isRTL
                        ? `ימים ${d}`
                        : `${d} days`
                  }
                  sx={isRTL ? { direction: "rtl" } : undefined}
                >
                  {d === 0
                    ? isRTL
                      ? "הכל"
                      : "All"
                    : isRTL
                      ? `ימים ${d}`
                      : `${d} days`}
                </ToggleButton>
              ),
            )}
          </ToggleButtonGroup>
        </Box>

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t("yourAnalytics")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 1.5,
            width: "100%",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          {/* סה"כ חוזים – אייקון, כותרת, נתונים ומד בשורה אחת */}
          <Card
            variant="outlined"
            sx={{
              bgcolor: "background.paper",
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: "10px",
                      bgcolor: theme.palette.primary.main,
                      color: "#fff",
                      flexShrink: 0,
                      "& svg": { width: 16, height: 16 },
                    }}
                  >
                    <ContractsIcon />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                    {t("totalContracts")}
                  </Typography>
                  <AnimatedCountWithLimit
                    value={stats?.contracts?.total || 0}
                    limit={contractsLimit}
                    duration={700}
                    showProgressBar={false}
                    isRTL={isRTL}
                    numberProps={{
                      component: "span",
                      variant: "body1",
                      fontWeight: 700,
                      sx: { fontSize: "1rem", lineHeight: 1 },
                    }}
                    limitProps={{
                      component: "span",
                      variant: "body1",
                      sx: { fontSize: "1rem", lineHeight: 1 },
                    }}
                    display="inline-flex"
                    alignItems="center"
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((stats?.contracts?.total || 0) / (contractsLimit || 1)) * 100)}
                  color="primary"
                  sx={{
                    width: "100%",
                    height: 8,
                    borderRadius: 1,
                  }}
                />
              </Box>
            </CardContent>
          </Card>
          {[
            {
              label: t("signed"),
              icon: CheckIcon,
              iconBg: theme.palette.success.main,
              value: (
                <AnimatedNumber
                  value={stats?.contracts?.signed || 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
              color: "success.main",
            },
            {
              label: t("pending"),
              icon: ClockIcon,
              iconBg: theme.palette.warning.main,
              value: (
                <AnimatedNumber
                  value={
                    (stats?.contracts?.pending || 0) +
                    (stats?.contracts?.viewed || 0)
                  }
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
              color: "warning.main",
            },
            {
              label: t("expired"),
              icon: ExpiredIcon,
              iconBg: theme.palette.error.main,
              value: (
                <AnimatedNumber
                  value={stats?.contracts?.expired || 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
              color: "error.main",
            },
          ].map((item) => {
            const IconComponent = item.icon;
            return (
              <Card
                key={item.label}
                variant="outlined"
                sx={{
                  bgcolor: "background.paper",
                  flex: "1 1 0",
                  minWidth: 0,
                }}
              >
                <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "10px",
                        bgcolor: item.iconBg,
                        color: "#fff",
                        flexShrink: 0,
                        "& svg": { width: 16, height: 16 },
                      }}
                    >
                      <IconComponent />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                      {item.label}
                    </Typography>
                    {item.value}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Overview: 2 cards on top row, rest on second row; icon + label + value in one line */}
        {(() => {
          const overviewItems = [
            {
              label: t("totalClients"),
              icon: ClientsIcon,
              iconBg: theme.palette.info?.main || theme.palette.primary.main,
              value: (
                <AnimatedNumber
                  value={stats?.clientsTotal ?? 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
            },
            {
              label: t("newClientsInPeriod"),
              icon: ClientsIcon,
              iconBg: theme.palette.info?.main || theme.palette.primary.main,
              value: (
                <AnimatedNumber
                  value={stats?.clientsNewInPeriod ?? 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
            },
            {
              label: t("contractsExpiringSoon"),
              icon: CalendarIcon,
              iconBg: theme.palette.warning.main,
              value: (
                <AnimatedNumber
                  value={stats?.contractsExpiringSoon ?? 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
            },
            {
              label: t("activeTemplates"),
              icon: DocIcon,
              iconBg:
                theme.palette.secondary?.main || theme.palette.primary.main,
              value: (
                <AnimatedNumber
                  value={stats?.templatesCount ?? 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
            },
            {
              label: t("webhooksSuccessRate"),
              icon: WebhooksIcon,
              iconBg: theme.palette.primary.main,
              value: (() => {
                const hasWebhooks =
                  stats?.webhooks?.total != null && stats.webhooks.total > 0;
                return (
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                  >
                    {hasWebhooks
                      ? `${stats.webhooks.successRate ?? 0}%`
                      : t("noWebhooksYet")}
                  </Typography>
                );
              })(),
            },
            {
              label: t("averageTimeToSign"),
              icon: CheckIcon,
              iconBg: theme.palette.success.main,
              value: (
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                >
                  {stats?.avgDaysToSign != null
                    ? `${stats.avgDaysToSign} ${t("days")}`
                    : "–"}
                </Typography>
              ),
            },
            {
              label: t("teamMembers"),
              icon: ContractsIcon,
              iconBg: theme.palette.text.secondary,
              value: (
                <AnimatedNumber
                  value={stats?.teamMembersCount ?? 0}
                  duration={700}
                  component={Typography}
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", lineHeight: 1, display: "inline-block" }}
                />
              ),
            },
          ];
          const renderCard = (item) => {
            const IconComponent = item.icon;
            return (
              <Card
                key={item.label}
                variant="outlined"
                sx={{
                  bgcolor: "background.paper",
                  flex: "1 1 0",
                  minWidth: 140,
                }}
              >
                <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "10px",
                        bgcolor: item.iconBg,
                        color: "#fff",
                        flexShrink: 0,
                        "& svg": { width: 16, height: 16 },
                      }}
                    >
                      <IconComponent />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                      {item.label}
                    </Typography>
                    {item.value}
                  </Box>
                </CardContent>
              </Card>
            );
          };
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1.5, width: "100%" }}>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
                {overviewItems.slice(0, 3).map(renderCard)}
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
                {overviewItems.slice(3).map(renderCard)}
              </Box>
            </Box>
          );
        })()}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            width: "100%",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <Card variant="outlined" sx={{ flex: "1 1 65%", minWidth: 0 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t("contractsOverTime")}
              </Typography>
              {stats?.daily?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                  data={stats.daily}
                  margin={{ top: 16, right: 0, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) => {
                      const d = new Date(v);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    width={28}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                    itemStyle={{ color: theme.palette.text.primary }}
                    labelStyle={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                    labelFormatter={(v) => formatDateDDMMYYYY(v)}
                    cursor={false}
                  />
                  <Bar
                    dataKey="created"
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                    name={isRTL ? "נוצרו" : "Created"}
                    fillOpacity={0.75}
                    activeBar={{ fillOpacity: 1 }}
                  />
                  <Bar
                    dataKey="signed"
                    fill={theme.palette.success.main}
                    radius={[4, 4, 0, 0]}
                    name={isRTL ? "נחתמו" : "Signed"}
                    fillOpacity={0.75}
                    activeBar={{ fillOpacity: 1 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box py={6} textAlign="center">
                <Typography color="text.secondary">{t("noData")}</Typography>
              </Box>
            )}
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: "1 1 35%", minWidth: 0, textAlign: "center" }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{ textAlign: "center" }}
              >
                {t("contractDistribution")}
              </Typography>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                      itemStyle={{ color: theme.palette.text.primary }}
                      labelStyle={{
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={1.5}
                  justifyContent="center"
                  sx={{ mt: 1 }}
                >
                  {pieData.map((entry, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${entry.name} (${entry.value})`}
                      sx={{
                        bgcolor: entry.color,
                        color: "#fff",
                        border: "none",
                      }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <Box py={6} textAlign="center">
                <Typography color="text.secondary">{t("noData")}</Typography>
              </Box>
            )}
            </CardContent>
          </Card>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("analyticsNote")}{" "}
          <a href="/docs/api" style={{ color: theme.palette.primary.main }}>
            {t("documentation")}
          </a>{" "}
          {t("forMoreInfo")}
        </Typography>
      </CardContent>
    </Box>
  );
};

export default CompanyDashboard;
